import { useCallback, useEffect, useRef, useState } from 'react';

type UseIntersectionObserverOptions = {
  rootMargin?: string;
  threshold?: number;
};

export const useIntersectionObserver = <
  TRoot extends HTMLElement = HTMLDivElement,
  TTarget extends HTMLElement = HTMLDivElement,
>(
  intersectingCallback: () => void,
  { rootMargin = '50px', threshold = 0 }: UseIntersectionObserverOptions = {},
) => {
  const rootRef = useRef<TRoot>(null);
  const [target, setTarget] = useState<TTarget | null>(null);

  useEffect(() => {
    if (!target) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          intersectingCallback();
        }
      },
      { root: rootRef.current, rootMargin, threshold },
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [target, intersectingCallback, rootMargin, threshold]);

  const observeRef = useCallback((element: TTarget | null) => {
    setTarget(element);
  }, []);

  return {
    observeRef,
    rootRef,
  };
};
